/* ── api.js ── */
// ══════════════════════════════════════════════════════════════
// 真實 API 資料層 —— 介面與 js/mock.js 完全相同。
// 做法：init() 一次抓齊目前單位的資料進快取，畫面同步讀快取；
//       寫入動作打真後端後重新 init()，app.js 的渲染邏輯不用改寫成非同步。
// admin 的目前檢視單位以 X-Unit-ID header 傳給後端（非 admin 後端會忽略）。
// ══════════════════════════════════════════════════════════════

const API_BASE = 'https://inventory-backend-production-0590.up.railway.app';
const TOKEN_KEY = 'jr_token';
const USER_KEY = 'jr_user';

async function apiFetch(path, { method = 'GET', body = null, unitId = null, timeoutMs = 0 } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (unitId != null) headers['X-Unit-ID'] = String(unitId);

  // 09/16 懸掛保險：手機切背景回前景後，請求可能永遠懸掛（不成功也不失敗）——
  // 呼叫端可帶 timeoutMs，逾時主動中止讓錯誤處理接手（預設 0＝不限時，行為不變）
  const ctrl = timeoutMs > 0 && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const tmr = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  let res;
  try {
    res = await fetch(API_BASE + path, {
      method, headers, body: body ? JSON.stringify(body) : null,
      signal: ctrl ? ctrl.signal : undefined,
    });
  } catch (e) {
    // fetch 本身失敗＝連不到伺服器（斷網／訊號不良）或逾時中止
    const err = new Error('連不上伺服器：請檢查網路連線（輸入中的資料不會遺失）');
    err.offline = true;
    throw err;
  } finally {
    if (tmr) clearTimeout(tmr);
  }

  if (res.status === 401 && token) {
    // 帶著 token 仍被拒＝token 過期或失效：清掉登入狀態回登入頁
    // （登入失敗的 401 沒有 token，往下走一般錯誤處理顯示「帳號或密碼錯誤」）
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    location.replace('login.html');
    throw new Error('請重新登入');
  }
  if (!res.ok) {
    let detail = `伺服器錯誤（${res.status}）`;
    try { detail = (await res.json()).detail || detail; } catch (e) { /* 非 JSON 回應 */ }
    throw new Error(detail);
  }
  return res.json();
}

// 後端異動列 → 前端格式（init 的整批快取與品項明細的單項查詢共用同一份對應）
function mapMovement(m) {
  return {
    id: m.id, item_id: m.item_id, item_name: m.item_name,
    type: m.movement_type, qty: Number(m.qty), note: m.note || '',
    occurred: m.occurred_on || '',
    by: m.created_by_name || '', at: fmtTime(m.created_at),
    atMs: Date.parse(m.created_at),   // 原始登記時間：主管作廢 7 天窗判斷用
    voided: !!m.is_voided, voidReason: m.void_reason || '',
    voidedBy: m.voided_by_name || '', voidedAt: m.voided_at ? fmtTime(m.voided_at) : '',
  };
}

const API = {
  // ── 快取：init() 後畫面同步讀取 ──
  _units: [],
  _inventory: [],     // 目前單位的庫存（含品項資訊）
  _movements: [],     // 目前單位的異動（不含已作廢）
  _movementsAll: [],  // 含已作廢（品項明細歷史劃線留痕用）
  _transfers: [],     // 目前單位相關調撥單（含 items）
  _counts: {},        // { item_id: actual_qty }（今日）
  _countReasons: {},  // { item_id: 誤差原因 }（今日）
  _unitId: null,
  offlineAt: null,    // 非 null＝離線模式，值為快照同步時間（ms）

  me() {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); }
    catch (e) { return null; }
  },

  async init(unitId) {
    try {
      return await this._initOnline(unitId);
    } catch (e) {
      // 斷網 → 回退到上次成功同步的快照，仍可離線查庫存
      if (e && e.offline) {
        const got = this._restoreSnapshot(unitId);
        if (got != null) return got;
      }
      throw e;
    }
  },

  async _initOnline(unitId) {
    // 09/17 效能：開機 7 支 API（units＋庫存＋異動＋調撥＋盤點＋效期＋廠商）合併成一支開機包——
    // 主機在美國，每趟來回 0.2 秒起跳，趟數是開機時間的主宰。
    // admin 記住的單位失效時由後端自動校正為第一個單位（原本前端先打 /units 的邏輯已搬進後端）。
    const today = new Date().toLocaleString('sv').slice(0, 10);
    const b = await apiFetch(`/inventory/boot?count_date=${today}`, { unitId });
    return this._applyBoot(b);
  },

  // 09/18 儲存提速第二波：開機包灌入抽成共用——寫入端點的 ?boot=1 回應（存＋刷新一趟完成）
  // 也走這一條，成功彈窗跳出時畫面數字保證是最新的（使用者訪談定案：等數字正確才報成功）
  _applyBoot(b) {
    let unitId;
    this._units = b.units.map(u => ({ id: u.id, code: u.code, name: u.name, type: u.unit_type }));
    this._unitId = unitId = b.unit_id;
    const inv = b.inventory, moves = b.movements, transfers = b.transfers,
      counts = b.counts, expiry = b.expiry || [], sup = b.suppliers || [];

    this._suppliers = (sup || []).map(s => ({
      id: s.id, name: s.name, contact: s.contact || '', phone: s.phone || '', note: s.note || '',
    }));

    this._expiry = (expiry || []).map(r => ({
      id: r.id, item_id: r.item_id, name: r.item_name, unit: r.base_unit,
      batch: r.batch_no || '', date: r.expiry_date, qty: Number(r.qty),
      daysLeft: Number(r.days_left),
    }));

    this._inventory = inv.map(r => ({
      id: r.item_id, name: r.item_name, spec: r.spec || '',
      unit: r.base_unit, cat: r.category, freq: r.count_freq, loc: r.location || '',
      safety: Number(r.safety_stock), qty: Number(r.current_qty), low: r.is_low,
      fav: !!r.is_favorite, favOrder: r.fav_order,
    }));

    // _movementsAll 含已作廢（品項明細歷史要劃線留痕）；_movements 只留有效帳（儀表板/計算用）
    this._movementsAll = moves.map(mapMovement);
    this._movements = this._movementsAll.filter(m => !m.voided);

    this._counts = {};
    this._countReasons = {};
    this._countAt = {};   // 儲存時間：週一沙拉「下午已複盤」判定用
    counts.forEach(c => {
      if (c.actual_qty !== null) {
        this._counts[c.item_id] = Number(c.actual_qty);
        this._countAt[c.item_id] = c.created_at;
      }
      if (c.diff_reason) this._countReasons[c.item_id] = c.diff_reason;
    });

    // 09/15 效能修正：明細已隨清單一次帶回（with_items），不再逐張補抓——
    // 開機 30 支 /transfers/{id} 歸零，開機時間不再隨調撥單數量變慢
    // 10/16：不再前端截 30——後端已改「未結案全帶＋已結案最新 30」，截了會把舊的未結案單又切掉
    this._transfers = transfers.map((t) => ({
      id: t.id, from_unit_id: t.from_unit_id, to_unit_id: t.to_unit_id,
      status: t.status, kind: t.kind || 'pull',
      need_date: t.need_date || null, need_slot: t.need_slot || '',   // 09/28 要貨時間（舊單 null＝未指定）
      note: t.note || '', by: '', at: fmtTime(t.created_at),
      items: (t.items || []).map(it => ({
        item_id: it.item_id, name: it.item_name,
        qty: Number(it.request_qty),
        approved: it.approved_qty == null ? undefined : Number(it.approved_qty),
        shipped: it.shipped_qty == null ? undefined : Number(it.shipped_qty),
        received: it.received_qty == null ? undefined : Number(it.received_qty),
      })),
    }));

    this.offlineAt = null;
    // 成功同步 → 存快照供離線查庫存（調撥/盤點屬即時資料不入快照）
    try {
      localStorage.setItem('jr_snap_' + unitId, JSON.stringify({
        at: Date.now(), units: this._units, inventory: this._inventory,
        movements: this._movements,
      }));
    } catch (e) { /* 空間不足不影響操作 */ }

    return unitId;   // 回傳實際生效的單位（可能已被校正）
  },

  // 離線回退：載入該單位（或最後使用單位）的快照
  _restoreSnapshot(unitId) {
    let raw = localStorage.getItem('jr_snap_' + unitId);
    if (!raw) {
      const last = parseInt(localStorage.getItem('jr_unit') || '0', 10);
      raw = localStorage.getItem('jr_snap_' + last);
      if (raw) unitId = last;
    }
    if (!raw) return null;
    try {
      const s = JSON.parse(raw);
      this._units = s.units;
      this._inventory = s.inventory;
      this._movements = s.movements;
      this._movementsAll = s.movements;   // 離線快照不含已作廢，歷史照常顯示有效帳
      this._transfers = [];
      this._counts = {};
      this._countReasons = {};
      this._expiry = [];
      this._suppliers = [];
      this._unitId = unitId;
      this.offlineAt = s.at;
      return unitId;
    } catch (e) { return null; }
  },

  // ── 讀取（同步，讀快取；unitId 參數為相容 mock 介面，實際以 init 的單位為準） ──
  units() { return this._units; },
  expiry() { return this._expiry || []; },   // 30 天內到期（含過期）入庫批次
  suppliers() { return this._suppliers || []; },   // 啟用中供應商（入庫下拉用）
  unit(id) { return this._units.find(u => u.id === id); },
  item(id) {
    const i = this._inventory.find(x => x.id === id);
    if (i) return i;
    // 調撥單裡可能有非本單位品項：從調撥明細找名稱
    for (const t of this._transfers) {
      const it = t.items.find(x => x.item_id === id);
      if (it) return { id, name: it.name, unit: '' };
    }
    return null;
  },
  inventory() { return this._inventory; },
  movements(unitId, itemId = null) {
    return itemId === null ? this._movements
      : this._movements.filter(m => m.item_id === itemId);
  },
  movementsAll(unitId, itemId = null) {
    return itemId === null ? this._movementsAll
      : this._movementsAll.filter(m => m.item_id === itemId);
  },
  // 品項明細用：直接向後端查「這個品項」的歷史，不要從 _movementsAll 篩。
  // 那份是全單位最近 100 筆，總店一天 30~50 筆、三天就滿了——冷門品項會整段查無紀錄，
  // 主管作廢權限開的是 7 天，看不到那筆就按不到，等於權限有一半是廢的。
  async itemMovements(itemId, limit = 50) {
    const rows = await apiFetch(
      `/inventory/movements?item_id=${itemId}&limit=${limit}`, { unitId: this._unitId });
    return rows.map(mapMovement);
  },
  getCount(unitId, date, itemId) {
    const v = this._counts[itemId];
    return v === undefined ? null : v;
  },
  getCountReason(itemId) { return this._countReasons[itemId] || ''; },
  getCountAt(itemId) { return (this._countAt || {})[itemId] || null; },
  transfers() { return this._transfers; },

  // ── 寫入（打真後端後重新整理快取） ──
  // 09/18 儲存提速：高頻寫入帶 ?boot=1，回應夾最新開機包→就地灌入，省掉第二趟重抓
  //（尖峰時省 0.3~0.7 秒）；後端沒回 boot（舊版/例外）就照舊補抓一趟，行為不變
  _afterWrite(r) {
    if (r && r.boot) { this._applyBoot(r.boot); return Promise.resolve(); }
    return this.init(this._unitId);
  },
  async addMovement(unitId, itemId, type, qty, note, user, extra = {}) {
    const r = await apiFetch('/inventory/movements?boot=1', {
      method: 'POST', unitId: this._unitId,
      body: {
        item_id: itemId, movement_type: type, qty: Math.abs(qty),
        note: note || '',
        batch_no: extra.batch || null, expiry_date: extra.expiry || null,
        supplier_id: extra.supplier || null,
        occurred_on: extra.occurred || null,
      },
    });
    await this._afterWrite(r);
  },

  // ── 供應商維護（require_manager；全公司共用目錄，停用不刪）──
  async listSuppliersAll() {
    const rows = await apiFetch('/suppliers?inactive=1', { unitId: this._unitId });
    return rows.map(s => ({
      id: s.id, name: s.name, contact: s.contact || '', phone: s.phone || '',
      note: s.note || '', active: !!s.is_active,
    }));
  },
  async createSupplier(data) {
    await apiFetch('/suppliers', { method: 'POST', unitId: this._unitId, body: data });
    await this.init(this._unitId);
  },
  async updateSupplier(id, patch) {
    await apiFetch(`/suppliers/${id}`, { method: 'PATCH', unitId: this._unitId, body: patch });
    await this.init(this._unitId);
  },

  // 作廢一筆異動（主管限入庫/領用、admin 全類型；原因必填≥5字，後端不刪資料只標記）
  async voidMovement(movementId, reason) {
    const r = await apiFetch(`/inventory/movements/${movementId}/void?boot=1`, {
      method: 'POST', unitId: this._unitId, body: { reason },
    });
    await this._afterWrite(r);
  },

  // 依老闆決策：盤點儲存同步修正帳面（apply_adjust: true），誤差品項需附原因
  async saveCounts(unitId, date, entries, user, reasons = {}) {
    const r = await apiFetch('/inventory/counts?boot=1', {
      method: 'POST', unitId: this._unitId,
      body: { count_date: date, counts: entries, reasons, apply_adjust: true },
    });
    await this._afterWrite(r);
    return r;   // {saved, adjusted}
  },

  async createTransfer(fromUnitId, toUnitId, items, note, user, need = null) {
    const r = await apiFetch('/transfers?boot=1', {
      method: 'POST', unitId: this._unitId,
      body: {
        from_unit_id: fromUnitId, note: note || '', items,
        // 09/28 要貨時間：{ date:'YYYY-MM-DD', slot:'早上'|'下午'|'皆可' }
        ...(need ? { need_date: need.date, need_slot: need.slot } : {}),
      },
    });
    await this._afterWrite(r);
  },

  // 寄出單：本單位主動送貨/寄放，送出即扣本單位庫存，等對方點收入帳
  async createSendTransfer(toUnitId, items, note, user) {
    const r = await apiFetch('/transfers/send?boot=1', {
      method: 'POST', unitId: this._unitId,
      body: { to_unit_id: toUnitId, items, note },
    });
    await this._afterWrite(r);
  },

  // 出貨方品項目錄（C 方案）：發申請時列「對方有什麼」
  // 2026-08-24 起附對方現量 srcQty（受控例外，僅此清單唯讀）；times/pinned → 前端組「常用品項」區
  async transferCatalog(fromUnitId) {
    const rows = await apiFetch(`/transfers/catalog?from_unit_id=${fromUnitId}`, { unitId: this._unitId });
    return rows.map(r => ({
      id: r.id, name: r.name, spec: r.spec || '', unit: r.base_unit, cat: r.category,
      loc: r.location || '',
      srcQty: r.current_qty != null ? Number(r.current_qty) : null,
      times: Number(r.times) || 0, pinned: !!r.pinned,
    }));
  },

  // 釘選/取消常用品項（記在「本單位 → 出貨單位」方向上）
  async toggleTransferPin(fromUnitId, itemId, pinned) {
    await apiFetch('/transfers/catalog/pin', {
      method: 'POST', unitId: this._unitId,
      body: { from_unit_id: fromUnitId, item_id: itemId, pinned },
    });
  },

  async transferAction(id, action, user, qtys = null) {
    // qtys：{item_id: 實際數量}——出貨填實出量、點收填實收量；null＝照預設（申請量/出貨量）
    const body = action === 'ship' ? { shipped: qtys || {} }
               : action === 'receive' ? { received: qtys || {} }
               : action === 'approve' ? { approved: qtys || {} } : {};
    const r = await apiFetch(`/transfers/${id}/${action}?boot=1`, {
      method: 'POST', unitId: this._unitId, body,
    });
    await this._afterWrite(r);
  },

  // ── 品項管理 ──
  // 本單位「已停用」品項清單（隨叫隨抓，不進快取）
  async listInactiveItems() {
    const rows = await apiFetch('/items?inactive=1', { unitId: this._unitId });
    return rows.map(r => ({
      id: r.id, name: r.name, spec: r.spec || '',
      unit: r.base_unit, cat: r.category, freq: r.count_freq,
      safety: Number(r.safety_stock),
    }));
  },

  async createItem(body) {
    await apiFetch('/items', { method: 'POST', unitId: this._unitId, body });
    await this.init(this._unitId);
  },

  // 目錄欄位（全公司共用）＋本單位設定（安全庫存/頻率）一次更新
  async updateItemFull(itemId, catalog, unitCfg) {
    await apiFetch(`/items/${itemId}`, { method: 'PATCH', unitId: this._unitId, body: catalog });
    await apiFetch(`/items/${itemId}/unit-config`, { method: 'PATCH', unitId: this._unitId, body: unitCfg });
    await this.init(this._unitId);
  },

  async updateUnitConfig(itemId, body) {
    await apiFetch(`/items/${itemId}/unit-config`, { method: 'PATCH', unitId: this._unitId, body });
    await this.init(this._unitId);
  },

  // ── Web Push（通知二階段：App 完全關閉也收得到調撥推播） ──
  async pushVapidKey() {
    return apiFetch('/push/vapid-key', { unitId: this._unitId });
  },
  async pushSubscribe(sub) {   // sub＝PushSubscription.toJSON()（endpoint＋keys）
    await apiFetch('/push/subscribe', { method: 'POST', unitId: this._unitId, body: sub });
  },

  // ── 重點物料看板（我的最愛） ──
  async setFavorite(itemId, isFav) {
    await apiFetch(`/items/${itemId}/favorite`, {
      method: 'POST', unitId: this._unitId, body: { is_favorite: isFav },
    });
    await this.init(this._unitId);
  },

  // 拖移排序：先更新本地快取（畫面即時），再送後端（不重抓、不打斷操作）
  async saveFavOrder(ids) {
    ids.forEach((id, idx) => {
      const it = this._inventory.find(x => x.id === id);
      if (it) it.favOrder = idx + 1;
    });
    await apiFetch('/items/favorites/order', {
      method: 'POST', unitId: this._unitId, body: { order: ids },
    });
  },

  // ── CSV 匯出（下載檔案，Excel 可直接開） ──
  async exportCSV(kind, filename) {
    const headers = {};
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) headers['Authorization'] = 'Bearer ' + token;
    if (this._unitId != null) headers['X-Unit-ID'] = String(this._unitId);
    const res = await fetch(`${API_BASE}/export/${kind}`, { headers });
    if (!res.ok) throw new Error(`匯出失敗（${res.status}）`);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await res.blob());
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  },
};

// 後端 created_at（ISO / UTC）→ 本地 'YYYY-MM-DD HH:mm'
function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 16).replace('T', ' ');
  return d.toLocaleString('sv').slice(0, 16);
}

// ── 登入頁使用 ──
async function apiLogin(username, password) {
  const r = await apiFetch('/auth/login', {
    method: 'POST', body: { username, password },
  });
  localStorage.setItem(TOKEN_KEY, r.token);
  localStorage.setItem(USER_KEY, JSON.stringify({
    id: r.user.id, name: r.user.display_name,
    role: r.user.role, unit_id: r.user.unit ? r.user.unit.id : null,
  }));
  return r.user;
}

function apiLogout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

;
/* ── notify.js ── */
/* 通知中心（09/10 老闆定案）：頂欄鈴鐺＋下拉選單，列「有動作要做」的待辦、點了直達對應系統分頁。
   規則（09/10 定案 5 類）：待審核申請（admin）／今日未評分／輪到本單位的調撥／今日每日盤點未完成／待驗收出貨單。
   低庫存、近效期屬常態警示不列入（紅點永遠亮就沒有警報意義）；零待辦不掛紅點。
   用法：頁面載入本檔後呼叫 notifyRender(summary)（summary＝/portal/summary 回包）；
   沒現成資料的頁面呼叫 notifyBoot() 自抓。掛載點＝頂欄 .huser（插在登出前）。 */
(function () {
  const CSS = `
  .ntf-wrap { position:relative; }
  .ntf-bell { position:relative; width:40px; height:40px; border-radius:50%; background:#fff; border:1.5px solid var(--line);
              display:flex; align-items:center; justify-content:center; cursor:pointer; flex:none; }
  .ntf-bell:active { transform:scale(.95); }
  .ntf-bell svg { width:20px; height:20px; fill:none; stroke:var(--ink); stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
  .ntf-bdg { position:absolute; top:-5px; right:-5px; min-width:19px; height:19px; border-radius:9999px; background:var(--red);
             color:#fff; font-size:.68rem; font-weight:900; display:flex; align-items:center; justify-content:center;
             padding:0 5px; border:2px solid var(--cream); }
  .ntf-menu { position:absolute; top:52px; right:-58px; width:min(340px, calc(100vw - 24px)); background:#fff;
              border:1px solid var(--line); border-radius:18px; padding:14px 14px 10px;
              box-shadow:0 14px 44px rgba(43,37,35,.22); z-index:40; display:none;
              transform-origin:top right; animation:ntfdrop .16s ease-out; }
  .ntf-menu.open { display:block; }
  .ntf-menu::before { content:''; position:absolute; top:-7px; right:var(--ntf-caret, 68px); width:14px; height:14px; background:#fff;
                      border-left:1px solid var(--line); border-top:1px solid var(--line); transform:rotate(45deg); }
  @keyframes ntfdrop { from { opacity:0; transform:scale(.96) translateY(-6px); } to { opacity:1; transform:none; } }
  .ntf-menu h3 { font-size:.98rem; font-weight:900; margin:0 0 2px; }
  .ntf-sub { color:var(--ink-soft, var(--soft)); font-weight:700; font-size:.76rem; margin-bottom:8px; }
  .ntf-grp { font-size:.78rem; font-weight:900; color:var(--ink-soft, var(--soft)); margin:12px 2px 6px; }
  .ntf-item { display:flex; align-items:center; gap:10px; width:100%; background:var(--cream); border:none; border-radius:12px;
              padding:12px 14px; margin-bottom:8px; cursor:pointer; text-align:left; font-family:inherit; }
  .ntf-item:active { transform:scale(.98); }
  .ntf-item .t { flex:1; font-weight:800; font-size:.88rem; color:var(--ink); }
  .ntf-item .n { background:var(--red); color:#fff; border-radius:9999px; min-width:22px; height:22px; padding:0 7px;
                 font-size:.75rem; font-weight:900; display:flex; align-items:center; justify-content:center; flex:none; }
  .ntf-item .arr { color:var(--ink-soft, var(--soft)); font-weight:900; }
  .ntf-empty { text-align:center; color:var(--green); font-weight:900; padding:22px 0 16px; }
  /* 09/10 bug：選單在 header 堆疊層（各頁 z=5~30）內，遮罩 z 太高會蓋住選單吃掉點擊——
     遮罩必須低於最低的 header z（=5），選單留在 header 內自然在遮罩之上 */
  .ntf-scrim { position:fixed; inset:0; z-index:4; display:none; }
  .ntf-scrim.open { display:block; }
  /* 窄螢幕（含頂欄折行變高的中間寬度）：選單貼齊視窗左右、頂距開啟時動態算（貼頂欄底）
     09/11 晚修：這裡原本寫死 right:96px，把 JS 算的 --ntf-caret 蓋掉——舊頂欄下 96 恰好貼近鈴鐺
     沒被發現，頂欄精簡後鈴鐺右移就偏了 25px+；改回吃變數（fallback 96） */
  @media (max-width:720px) {
    .ntf-menu { position:fixed; top:70px; left:12px; right:12px; width:auto; }
    .ntf-menu::before { right:var(--ntf-caret, 96px); }
  }`;

  function mount() {
    if (document.getElementById('ntf-bell')) return true;
    // 掛載點：入口/人事/出貨＝.huser 最前（名字左邊，與庫存頁一致——09/10 回饋統一順序）；
    // 庫存＝#user-chip 前（chip 內容由 app.js 重繪，鈴鐺放旁邊當兄弟才不會被洗掉）
    let host = document.querySelector('.huser');
    let before = host ? host.firstElementChild : null;
    let invMode = false;
    if (!host) {
      const uc = document.getElementById('user-chip');
      if (!uc) return false;
      host = uc.parentElement; before = uc; invMode = true;
    }
    const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
    const wrap = document.createElement('span');
    wrap.className = 'ntf-wrap';
    wrap.innerHTML =
      '<span class="ntf-bell" id="ntf-bell" title="待處理事項">' +
      '<svg viewBox="0 0 24 24"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>' +
      '<span class="ntf-bdg" id="ntf-bdg" style="display:none;"></span></span>' +
      '<div class="ntf-menu" id="ntf-menu"><h3>待處理事項</h3>' +
      '<div class="ntf-sub">點項目直接前往處理</div><div id="ntf-body"></div></div>';
    // 09/10 位置一致修正：庫存頁靠 #user-chip 的 margin-left:auto 推右——鈴鐺插它前面會被留在左邊；
    // 把推右職責移到鈴鐺（鈴鐺 auto、user-chip 歸零），四頁鈴鐺都落在「說明/登出」左側
    if (invMode) { wrap.style.marginLeft = 'auto'; before.style.marginLeft = '0'; }
    host.insertBefore(wrap, before);
    const scrim = document.createElement('div');
    scrim.className = 'ntf-scrim'; scrim.id = 'ntf-scrim';
    document.body.appendChild(scrim);
    document.getElementById('ntf-bell').onclick = (e) => { e.stopPropagation(); toggle(); };
    scrim.onclick = close;
    return true;
  }
  function toggle() {
    const m = document.getElementById('ntf-menu');
    if (m.classList.contains('open')) return close();
    // 09/10 跑版修正：窄螢幕選單改 fixed、頂距開啟當下動態算；
    // 09/11 晚回饋：改貼「鈴鐺底部」而非頂欄底部——箭頭要像從鈴鐺長出來的（頂欄折行變高也自然跟著對）
    if (window.innerWidth <= 720) {
      const bellR = document.getElementById('ntf-bell').getBoundingClientRect();
      m.style.top = (bellR.bottom + 10) + 'px';   // 箭頭尖端（top-7）落在鈴鐺下緣 +3px，視覺相連
    } else { m.style.top = ''; }
    m.classList.add('open');
    document.getElementById('ntf-scrim').classList.add('open');
    // 09/11 晚修：箭頭改「開啟後量選單實際右緣」回推距右值——不再用視窗寬假設（實測差 8px），
    // 任何寬度都以真實位置對準鈴鐺中心。選單縮放動畫 transform-origin 在右上角，右緣不動、量測穩定。
    const b = document.getElementById('ntf-bell').getBoundingClientRect();
    const mr = m.getBoundingClientRect();
    m.style.setProperty('--ntf-caret', Math.max(14, mr.right - (b.left + b.width / 2) - 7) + 'px');
  }
  function close() {
    document.getElementById('ntf-menu').classList.remove('open');
    document.getElementById('ntf-scrim').classList.remove('open');
  }
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // 點項目：跳對應頁；已經在同一頁時（例如出貨頁點「待驗收」）改成重新整理帶最新資料。
  // 09/11：庫存類項目可帶 unitId——admin 點「辦公室調撥」直接切到該單位落地（jr_unit＝庫存記住的單位鍵）
  window.ntfGo = (href, unitId) => {
    if (unitId) { try { localStorage.setItem('jr_unit', unitId); } catch (e) {} }
    const target = href.split('#')[0];
    const same = location.pathname.endsWith('/' + target);
    location.href = href;
    if (same) location.reload();
  };

  // summary（/portal/summary 回包）→ 待辦清單
  function buildItems(sum) {
    const admin = sum.role === 'admin';
    const items = [];
    if (admin && sum.pending_requests > 0)
      items.push({ g: '人事評分', t: '待審核申請', n: sum.pending_requests, href: 'scoring.html#review' });
    for (const u of (sum.units || [])) {
      const label = (sum.units.length > 1 ? u.name + '：' : '');
      const hr = u.hr || {}, inv = u.inventory || {}, ship = u.shipping;
      const noScoreDay = hr.holiday || hr.rest || (hr.mgr_off && u.code !== 'FACTORY');
      if ((hr.duty || []).length > 0 && !hr.scored && !noScoreDay)
        items.push({ g: '人事評分', t: label + '今日未評分', href: 'scoring.html#' + (admin ? 'daily' : 'score') });
      if (inv.daily_total > 0 && inv.daily_counted < inv.daily_total && !hr.holiday)
        items.push({ g: '庫存系統', t: label + '每日盤點未完成（' + inv.daily_counted + '/' + inv.daily_total + ' 已填）', href: 'inventory.html#counts', uid: u.unit_id });
      if (inv.transfers_todo > 0)
        items.push({ g: '庫存系統', t: label + '輪到處理的調撥', n: inv.transfers_todo, href: 'inventory.html#transfers', uid: u.unit_id });
      // 09/11 老闆回饋：補「待配送」（開了單通知才看得到）；待驗收照舊
      if (ship && ship.to_ship > 0)
        items.push({ g: '出貨管理', t: label + '今日待配送出貨單', n: ship.to_ship, href: 'shipping.html' });
      if (ship && ship.to_confirm > 0)
        items.push({ g: '出貨管理', t: label + '待驗收出貨單', n: ship.to_confirm, href: 'shipping.html' });
    }
    // 09/11 老闆回饋：辦公室的調撥也要進通知（admin 專屬；點了到庫存切辦公室處理）
    if (sum.office && sum.office.transfers_todo > 0)
      items.push({ g: '庫存系統', t: sum.office.name + '：輪到處理的調撥', n: sum.office.transfers_todo, href: 'inventory.html#transfers', uid: sum.office.id });
    // 10/15 使用者回饋：admin 多單位時逐單位組清單會讓分類標題重複（人事、庫存、人事、庫存…）
    // ——照分類穩定排序（同分類內維持原本的單位順序），每個標題只出現一次
    const GRP_ORDER = ['人事評分', '庫存系統', '出貨管理'];
    const gi = (g) => { const i = GRP_ORDER.indexOf(g); return i < 0 ? GRP_ORDER.length : i; };
    items.sort((a, b) => gi(a.g) - gi(b.g));
    return items;
  }

  let _lastKeys = null;   // 上一輪待辦鍵集合（偵測「新事項」用）
  window.notifyRender = (sum) => {
    if (!mount()) return;
    const items = buildItems(sum);
    const bdg = document.getElementById('ntf-bdg');
    bdg.style.display = items.length ? 'flex' : 'none';
    // 09/18 質感包①#9：待辦「變多」時紅點呼吸兩下就停（首次載入不呼吸）
    if (_lastKeys && items.length > Number(bdg.textContent || 0)) {
      bdg.classList.remove('ntf-bdg-pulse'); void bdg.offsetWidth; bdg.classList.add('ntf-bdg-pulse');
    }
    bdg.textContent = items.length;
    const body = document.getElementById('ntf-body');
    // 09/17 共用層健檢：選單開啟中只更新紅點數字、不重排項目——
    // 60 秒輪詢會讓項目在使用者手指底下換位置（點下去跳錯頁）；關閉後下一輪自然更新
    if (document.getElementById('ntf-menu').classList.contains('open')) return;
    if (!items.length) { body.innerHTML = '<div class="ntf-empty">✓ 目前沒有待處理事項</div>'; }
    else {
      let h = '', lastG = null;
      for (const i of items) {
        if (i.g !== lastG) { lastG = i.g; h += '<div class="ntf-grp">' + i.g + '</div>'; }
        h += '<button class="ntf-item" onclick="ntfGo(\'' + i.href + '\',' + (i.uid || 'null') + ')"><span class="t">' + esc(i.t) + '</span>' +
             (i.n ? '<span class="n">' + i.n + '</span>' : '') + '<span class="arr">›</span></button>';
      }
      body.innerHTML = h;
    }
    // 09/10 後場回饋（司機送達沒看到）：輪詢間偵測到「新出現的待辦」→ 頁內提示，不靠系統通知
    const keys = new Set(items.map(i => i.t));
    if (_lastKeys) {
      const fresh = items.filter(i => !_lastKeys.has(i.t));
      if (fresh.length && typeof toast === 'function')
        toast('有新的待處理事項：' + fresh[0].t + (fresh.length > 1 ? ' 等 ' + fresh.length + ' 件' : '') + '——點右上角鈴鐺查看');
    }
    _lastKeys = keys;
  };

  // 09/16 開機去重：入口剛抓過的摘要 30 秒內直接沿用（sessionStorage，綁 token 防換帳號吃到別人的）
  const SUM_CK = 'jr_sum_cache';
  const tokenTag = () => { try { return (localStorage.getItem('jr_token') || '').slice(0, 16); } catch (e) { return ''; } };
  function readSumCache() {
    try {
      const o = JSON.parse(sessionStorage.getItem(SUM_CK));
      if (o && o.u === tokenTag() && Date.now() - o.t < 30_000) return o.s;
    } catch (e) { /* 無快取/格式壞：當沒有 */ }
    return null;
  }
  window.ntfCacheSum = (s) => { try { sessionStorage.setItem(SUM_CK, JSON.stringify({ t: Date.now(), u: tokenTag(), s })); } catch (e) {} };

  // 自抓摘要（庫存/人事/出貨頁掛載用；入口由 portal.js 餵第一份後同樣進入輪詢）
  async function fetchAndRender() {
    try {
      const s = await apiFetch('/portal/summary');
      window.ntfCacheSum(s);
      window.notifyRender(s);
    } catch (e) { /* 離線/未登入：不顯示鈴鐺 */ }
  }
  // 09/18 更新背景預載（效能第一波③）：比對部署清單 precache.json，
  // 有新版就在背景把 CSS/JS 先抓進快取——下次開頁直接用，不再「更新日特別慢」。
  // jr_prefetched_v＝已預載過的版號（登記於 docs/共用層守則.md 記憶鍵表）
  async function prefetchNewVersion() {
    try {
      const m = await (await fetch('precache.json?ts=' + Date.now(), { cache: 'no-store' })).json();
      if (!m || !m.v) return;
      let seen = null, seenUrls = [];
      try {
        seen = localStorage.getItem('jr_prefetched_v');
        seenUrls = JSON.parse(localStorage.getItem('jr_prefetched_urls') || '[]');
      } catch (e) {}
      if (seen === m.v) return;
      // 09/21 逐檔版號：只抓「網址（＝內容雜湊）真的變了」的檔——沒變的檔一個位元組都不重抓
      const seenSet = new Set(seenUrls);
      const need = (m.urls || []).filter((u) => !seenSet.has(u));
      await Promise.all(need.map((u) => fetch(u).catch(() => {})));   // 失敗就算了，下次再試
      try {
        localStorage.setItem('jr_prefetched_v', m.v);
        localStorage.setItem('jr_prefetched_urls', JSON.stringify(m.urls || []));
      } catch (e) {}
    } catch (e) { /* 清單拿不到（離線等）：不打擾，下次輪詢再試 */ }
  }
  window.ntfPrefetch = prefetchNewVersion;   // QA 用
  setTimeout(prefetchNewVersion, 8_000);     // 開機安頓後才做，不搶開機頻寬

  let _poll = null, _tick = 0;
  function startPoll() {
    if (_poll) return;
    _poll = setInterval(() => {
      if (document.hidden) return;
      fetchAndRender();
      if (++_tick % 10 === 0) prefetchNewVersion();   // 每 10 分鐘順手檢查一次新版
    }, 60_000);   // 60 秒自動更新（背景分頁不打）
  }
  window.notifyBoot = async () => {
    const c = readSumCache();
    if (c) { window.notifyRender(c); startPoll(); return; }   // 30 秒內入口抓過：沿用，60 秒輪詢照常接手
    await fetchAndRender(); startPoll();
  };
  // 入口（portal.js 餵資料）也要進入輪詢
  const _origRender = window.notifyRender;
  window.notifyRender = (sum) => { _origRender(sum); startPoll(); };
})();

;
/* ── who.js ── */
// toast 共用殼（09/24 code review ⑥：原本同一段 popover 邏輯貼在五個檔案已開始各自走樣）。
// 為什麼要 popover：dialog 在 top layer，#toast 的 z-index 再高都蓋不過去，
// 比彈窗晚進 top layer 才疊得上面（09/22 品管會審實案）；舊瀏覽器沒有 showPopover 就走原 z-index。
// 各頁 toast 自管文字與顯示時長，只把「怎麼開、怎麼收」交給這兩支。
window.toastPop = (t) => {
  if (t.showPopover) { try { t.showPopover(); } catch (e) {} void t.offsetWidth; }  // 回流讓 .show 過場生效
  clearTimeout(t._pop);
  t.classList.add('show');
};
window.toastPopHide = (t) => {
  t.classList.remove('show');
  // 300ms＝退場動畫跑完再關 popover（不殘留 top layer）；期間再顯示會被 toastPop 清掉這個計時器
  if (t.hidePopover) t._pop = setTimeout(() => { try { t.hidePopover(); } catch (e) {} }, 300);
};

// 全站身分徽章（09/11 老闆規格）：長職稱＋常駐登出/說明鈕 → 單字徽章（管/總/後），點開才出現選單
// 老闆的邏輯：「這個按鈕到底多常被按到？幾乎沒人按就不需要一直在版面上佔位子」——說明/登出全收進徽章
// 四頁共用（入口/庫存/人事/出貨），比照 notify.js 模式：CSS 自注入＋各頁 boot 時呼叫 whoBoot()
// whoBoot({ badge:'管'|'總'|'後', name:'顯示名稱', items:[{label, run}] })——渲染進 #who-wrap
(function () {
  const st = document.createElement('style');
  st.textContent = `
  .who-rel { position:relative; display:inline-flex; }
  .who-badge { width:40px; height:40px; border-radius:50%; background:var(--yellow,#FFD41D); color:var(--ink,#2B2523);
    display:inline-flex; align-items:center; justify-content:center; font-weight:900; font-size:1.02rem;
    cursor:pointer; flex:none; user-select:none; box-shadow:inset 0 0 0 1.5px rgba(0,0,0,.06); transition:transform .05s ease; }
  .who-badge:active { transform:scale(.94); }
  /* 09/18 質感包②#10：毛玻璃小卡＋spring 展開（reduced-motion 時關動畫） */
  /* 09/18 回饋：.85 太透→.94；09/21 review 修（127-③）：.94 下背景模糊已近不可見卻照付
     合成成本（低階平板開選單會微卡）——拆掉 backdrop-filter，玻璃感由邊緣光＋陰影承擔 */
  .who-menu { position:absolute; top:calc(100% + 10px); right:0; background:rgba(255,255,255,.97);
    border:1px solid var(--line,#EFE6CF); border-radius:14px;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.7), 0 12px 30px rgba(0,0,0,.16);
    padding:6px; min-width:148px; z-index:80; display:none; transform-origin:top right; }
  .who-menu.open { display:block; animation:whoPop .26s cubic-bezier(.3,1.4,.5,1); }
  @keyframes whoPop { from { opacity:0; transform:scale(.88) translateY(-4px); } }
  @media (prefers-reduced-motion: reduce) { .who-menu.open { animation:none; } }
  .who-name { font-weight:800; font-size:.78rem; color:var(--ink-soft,#8a8171); padding:8px 12px 4px; white-space:nowrap; }
  .who-menu button { display:block; width:100%; text-align:left; background:none; border:none; font:inherit; font-weight:800;
    font-size:.92rem; padding:11px 12px; border-radius:9px; cursor:pointer; color:var(--ink,#2B2523); min-height:44px; white-space:nowrap; }   /* 10/16 品管會審：「檢視：總店視角」不拆行，選單跟著字寬撐開 */
  .who-menu button:hover { background:#F8F3E4; }`;
  document.head.appendChild(st);
})();
window.whoBoot = function (opts) {
  const host = document.querySelector(opts.el || '#who-wrap');
  if (!host) return;
  const escW = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  host.innerHTML = '<span class="who-rel">' +
    '<span class="who-badge">' + escW(opts.badge) + '</span>' +
    '<div class="who-menu">' +
      (opts.name ? '<div class="who-name">' + escW(opts.name) + '</div>' : '') +
      opts.items.map((it, i) => '<button type="button" data-wi="' + i + '">' + escW(it.label) + '</button>').join('') +
    '</div></span>';
  const menu = host.querySelector('.who-menu');
  host.querySelector('.who-badge').onclick = () => menu.classList.toggle('open');
  host.querySelectorAll('[data-wi]').forEach(b => {
    b.onclick = () => { menu.classList.remove('open'); opts.items[+b.dataset.wi].run(); };
  });
};
// 點徽章以外的地方＝收選單（capture：搶在其他 handler 前）
document.addEventListener('pointerdown', (e) => {
  document.querySelectorAll('.who-menu.open').forEach(m => {
    if (!e.target.closest('.who-rel')) m.classList.remove('open');
  });
}, { capture: true });

;
/* ── push.js ── */
/* 推播訂閱共用層（10/26 從 inv-transfer.js 搬出；打包進 core，入口／庫存／人事／出貨都載得到）
   背景：推播是三個系統共用（調撥、出貨、人事未評提醒），「開啟通知」原本只在庫存調撥頁，
   10/26 使用者定案搬到入口首頁問候語下方（A 版橫幅）；Railway 10/21 事件後 VAPID 鑰匙要換，
   換了手機上舊訂閱全失效但通知權限不會失效——sync() 發現訂閱綁的公鑰跟伺服器不同就退訂重訂，
   已開過通知的人什麼都不用按。
   用法：
     jrPush.renderBar(elId, unitId)  入口首頁橫幅（未開→按鈕；已開→一行＋靜音；被封鎖→提示）
     jrPush.sync(unitId)             開頁時呼叫：已授權的裝置確保訂閱有效（鑰匙換了自動補訂）
     jrPush.enable(unitId)           使用者按「開啟通知」
     jrPush.toggleMute()／muted()    提示音靜音（jr_notify_mute，共用層守則已登記）
   unitId：admin 的 /push/subscribe 要 X-Unit-ID（任一有效單位即可，admin 訂閱收全部單位的推播）；非 admin 可省略。 */
(function () {
  const CSS = `
  .push-bar { display:flex; align-items:center; gap:10px; background:#fff; border:1.5px solid var(--yellow); border-radius:14px;
              padding:10px 14px; margin-bottom:16px; font-size:.88rem; font-weight:800; }
  .push-bar.on { border-color:var(--line); background:#E7F4EC; }
  .push-bar.blk { border-color:var(--line); background:#FBEAE8; }
  .push-bar svg { width:20px; height:20px; flex:none; fill:none; stroke:var(--ink); stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
  .push-bar .tx { flex:1; min-width:0; } .push-bar .tx small { display:block; font-weight:600; color:var(--ink-soft, var(--soft)); font-size:.76rem; }
  .push-bar .btn { white-space:nowrap; }
  @media (prefers-reduced-motion: no-preference) { .push-bar { animation:greetIn .35s ease-out .18s both; } }`;
  const BELL = '<svg viewBox="0 0 24 24"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';
  let cssDone = false;
  const say = (m) => { if (typeof window.toast === 'function') window.toast(m); };
  const muted = () => { try { return localStorage.getItem('jr_notify_mute') === '1'; } catch (e) { return false; } };

  // base64url → bytes（pushManager.subscribe 的 applicationServerKey）
  function keyToBytes(b64) {
    const pad = '='.repeat((4 - b64.length % 4) % 4);
    const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  }
  // 訂閱綁的公鑰（ArrayBuffer）是否等於伺服器現在的公鑰（base64url 字串）——鑰匙換過就不等
  function keyMatches(buf, key) {
    if (!buf || !key) return false;
    const a = new Uint8Array(buf), b = keyToBytes(key);
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  function state() {
    if (!('Notification' in window)) return 'none';   // 裝置不支援（舊 iOS 未加入主畫面）
    return Notification.permission;                   // default／granted／denied
  }

  // 已授權的裝置：確保伺服器有這台裝置的有效訂閱。鑰匙不同→退訂重訂；每次呼叫順便 upsert 使用者／單位。
  async function sync(unitId) {
    try {
      if (!('PushManager' in window) || !navigator.serviceWorker || state() !== 'granted') return false;
      const reg = await navigator.serviceWorker.ready;
      const { key } = await apiFetch('/push/vapid-key');
      let sub = await reg.pushManager.getSubscription();
      if (sub && !keyMatches(sub.options && sub.options.applicationServerKey, key)) {
        try { await sub.unsubscribe(); } catch (e) { /* 退訂失敗也照樣重訂 */ }
        sub = null;
      }
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(key) });
      await apiFetch('/push/subscribe', { method: 'POST', unitId: unitId ?? null, body: sub.toJSON() });
      return true;
    } catch (e) { return false; }   // 靜默（iOS<16.4、瀏覽器不支援、離線、後端沒設鑰匙）
  }

  // 使用者主動按「開啟通知」才要求授權（答應率較高、不擾人）
  async function enable(unitId) {
    if (!('Notification' in window)) { say('此裝置或瀏覽器不支援通知'); return 'none'; }
    let p = Notification.permission;
    if (p === 'default') p = await Notification.requestPermission();
    if (p === 'granted') { say('已開啟通知'); if (typeof window.playChime === 'function') window.playChime(); await sync(unitId); }
    else say('通知未開啟——可到手機設定開啟');
    return p;
  }

  function toggleMute() {
    const m = !muted();
    try { localStorage.setItem('jr_notify_mute', m ? '1' : '0'); } catch (e) {}
    say(m ? '提示音已靜音' : '提示音已開啟');
    if (!m && typeof window.playChime === 'function') window.playChime();
    return m;
  }

  // 入口首頁橫幅（A 版）：狀態變了只重畫這一條
  function barHTML() {
    const s = state();
    if (s === 'none') return '';
    if (s === 'default') return `<div class="push-bar">${BELL}<div class="tx">開啟通知<small>有調撥單、出貨、要評分的提醒會直接跳到手機</small></div>` +
      `<button class="btn btn-primary btn-44" onclick="jrPush.enableFromBar()">開啟通知</button></div>`;
    if (s === 'denied') return `<div class="push-bar blk">${BELL}<div class="tx">通知被手機封鎖<small>到「設定 → 九如管理系統 → 通知」打開</small></div></div>`;
    return `<div class="push-bar on">${BELL}<div class="tx">通知已開啟・這台裝置會收到提醒${muted() ? '<small>提示音已靜音</small>' : ''}</div>` +
      `<button class="btn btn-ghost btn-44" onclick="jrPush.muteFromBar()">${muted() ? '取消靜音' : '靜音'}</button></div>`;
  }
  let barEl = null, barUnit = null;
  function renderBar(elId, unitId) {
    const el = document.getElementById(elId); if (!el) return;
    if (!cssDone) { const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st); cssDone = true; }
    barEl = el; barUnit = unitId ?? null;
    el.innerHTML = barHTML();
  }
  const repaint = () => { if (barEl) barEl.innerHTML = barHTML(); };

  window.jrPush = {
    state, sync, enable, toggleMute, muted, renderBar, keyMatches,
    enableFromBar: async () => { await enable(barUnit); repaint(); },
    muteFromBar: () => { toggleMute(); repaint(); },
  };
})();
